/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../theme';
import { CloseIcon, DivideIcon, MinusIcon, PlusIcon } from '../icons';
import BackSpaceIcon from '../icons/BackSpaceIcon';
import { KeyboardButton } from './StyledComponents';
import { KeyboardKey } from './types';
import { OPERATORS } from './useCalculator';

interface KeyboardLayoutProps {
  onNumberPress: (num: string) => void;
  onDecimal: () => void;
  onOperationPress: (operation: string) => void;
  onBackspace: () => void;
  onClear: () => void;
  onTripleZero: () => void;
  onDone: () => void;
  onEquals?: () => void;
  currentValue?: string;
}

export const KeyboardLayout: React.FC<KeyboardLayoutProps> = ({
  onNumberPress,
  onDecimal,
  onOperationPress,
  onBackspace,
  onClear,
  onTripleZero,
  onDone,
  onEquals,
  currentValue = '',
}) => {
  const { i18n } = useTranslation(['common']);

  const handleAction = (action: () => void) => {
    action();
  };

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;

      // Prevent default behavior for calculator keys
      if (/^[0-9+\-*/.,]$/.test(key) || key === 'Enter' || key === 'Escape' || key === 'Backspace' || key === 'Delete') {
        e.preventDefault();
      }

      // Handle number keys (0-9)
      if (/^[0-9]$/.test(key)) {
        onNumberPress(key);
        return;
      }

      // Handle decimal point
      if (key === '.' || key === ',') {
        onDecimal();
        return;
      }

      // Handle operators
      if (key === '+') {
        onOperationPress('+');
        return;
      }

      if (key === '-') {
        onOperationPress('-');
        return;
      }

      if (key === '*') {
        onOperationPress('×');
        return;
      }

      if (key === '/') {
        onOperationPress(':');
        return;
      }

      // Handle Enter key (equals/done)
      if (key === 'Enter') {
        onDone();
        return;
      }

      // Handle Escape key (clear)
      if (key === 'Escape') {
        onClear();
        return;
      }

      // Handle Backspace
      if (key === 'Backspace') {
        onBackspace();
        return;
      }

      // Handle Delete key (clear)
      if (key === 'Delete') {
        onClear();
        return;
      }
    };

    // Add event listener and focus on mount
    const el = document.getElementById('keyboard-layout');
    el?.addEventListener('keydown', handleKeyDown);
    el?.setAttribute('tabindex', '0');
    el?.focus();

    // Cleanup
    return () => {
      el?.removeEventListener('keydown', handleKeyDown);
    };
  }, [onNumberPress, onDecimal, onOperationPress, onBackspace, onClear, onTripleZero, onDone]);

  // Get decimal separator based on current language
  const getDecimalSeparator = () => {
    return i18n.language === 'vi' ? ',' : '.';
  };

  // Determine if current value has an expression
  const isOnlyMinus = currentValue?.trim() === '-';
  const isNumericString = !Number.isNaN(Number(currentValue));
  const containsAnyOperator = OPERATORS.some(operator => currentValue.includes(operator));
  const hasExpression = !isOnlyMinus && !isNumericString && containsAnyOperator;
  const doneButtonText = hasExpression ? '=' : 'Xong';
  const doneButtonAction = hasExpression ? (onEquals || onDone) : onDone;

  const keyboardLayout: KeyboardKey[] = [
    { label: <Typography fontSize={28} sx={{ fontWeight: 400, textTransform: 'uppercase' }}>C</Typography>, type: 'action', action: onClear },
    { label: <DivideIcon fontSize={32} />, type: 'operation', action: () => onOperationPress(':') },
    { label: <CloseIcon fontSize={32} />, type: 'operation', action: () => onOperationPress('×') },
    { label: <BackSpaceIcon fontSize={32} />, type: 'action', action: onBackspace },

    { label: '1', type: 'default', action: () => onNumberPress('1') },
    { label: '2', type: 'default', action: () => onNumberPress('2') },
    { label: '3', type: 'default', action: () => onNumberPress('3') },
    { label: <MinusIcon fontSize={32} />, type: 'operation', action: () => onOperationPress('-') },

    { label: '4', type: 'default', action: () => onNumberPress('4') },
    { label: '5', type: 'default', action: () => onNumberPress('5') },
    { label: '6', type: 'default', action: () => onNumberPress('6') },
    { label: <PlusIcon fontSize={32} />, type: 'operation', action: () => onOperationPress('+') },

    { label: '7', type: 'default', action: () => onNumberPress('7') },
    { label: '8', type: 'default', action: () => onNumberPress('8') },
    { label: '9', type: 'default', action: () => onNumberPress('9') },
    { label: doneButtonText, type: 'done', action: doneButtonAction, rowSpan: 2 },

    { label: <Typography fontSize={32} sx={{ fontWeight: 400 }}>{getDecimalSeparator()}</Typography>, type: 'default', action: onDecimal },
    { label: '0', type: 'default', action: () => onNumberPress('0') },
    { label: '000', type: 'default', action: onTripleZero }
  ];

  return (
    <Box
      id="keyboard-layout"
      role="application"
      aria-label="currency-input-keyboard"
      sx={{
        p: 1.5,
        gap: 1,
        width: '100%',
        display: 'grid',
        outline: 'none',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gridTemplateRows: 'repeat(5, 1fr)',
        backgroundColor: theme.palette.navy[400],
        gridTemplateAreas: `
          "a b c d"
          "e f g h"
          "i j k l"
          "m n o p"
          "q r s p"
        `,
      }}
    >
      {keyboardLayout.map((key, keyIndex) => {
        const gridAreas = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's'];
        return (
          <Box key={keyIndex} sx={{ gridArea: gridAreas[keyIndex] }}>
            <KeyboardButton
              fullWidth
              buttonType={key.type as any}
              onClick={(e) => {
                e.preventDefault();
                handleAction(key.action);
              }}
              disableRipple
              disableTouchRipple
              disableFocusRipple
              disableElevation
              sx={{
                height: '100%',
                borderRadius: 1,
                backgroundColor: key.type === 'done' ? theme.palette.navy[900] : theme.palette.common.white,
                color: key.type === 'done' ? theme.palette.common.white : theme.palette.text.primary,
                fontSize: 20,
                fontWeight: 500,
                boxShadow: theme.shadows[2],
                border: 'none !important',
                touchAction: 'manipulation',
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                '&:hover': {
                  backgroundColor: key.type === 'done' ? theme.palette.navy[900] : theme.palette.common.white,
                  color: key.type === 'done' ? theme.palette.common.white : theme.palette.text.primary,
                  boxShadow: theme.shadows[2],
                },
                '&:active': {
                  backgroundColor: key.type === 'done' ? theme.palette.navy[900] : theme.palette.common.white,
                  color: key.type === 'done' ? theme.palette.common.white : theme.palette.text.primary,
                  boxShadow: theme.shadows[2],
                },
                '&:focus': {
                  backgroundColor: key.type === 'done' ? theme.palette.navy[900] : theme.palette.common.white,
                  color: key.type === 'done' ? theme.palette.common.white : theme.palette.text.primary,
                  boxShadow: theme.shadows[2],
                }
              }}
            >
              {key.label}
            </KeyboardButton>
          </Box>
        );
      })}
    </Box>
  );
}; 